import React from "react";
import { Button, Modal, Col, Row, Collapse, Form } from "antd";
import _ from "lodash";
import {
  ExclamationCircleOutlined,
  DoubleRightOutlined,
} from "@ant-design/icons";

import { API, request } from "../../../config/API";
import authHeader from "../../../redux/services/authHeader";
import { INITIAL_DATA } from "./INITIAL_DATA";
import {
  Address,
  Company,
  Email,
  Gallery,
  JobTitle,
  MobileNumber,
  Name,
  PhoneNumber,
  PostalAddress,
  SocialLinks,
  Summary,
  Theme,
  Websites,
  WaveCodeStyle,
} from "./MassEditComponent";

import { COLORS } from "../../../config/theme";
import { WHITE_LABEL_ADMIN } from "../../../config/roles";
import { useSelector } from "react-redux";

const { confirm, success, error } = Modal;
const { primaryDark } = COLORS;

const MassEditModal = ({
  showMassEditModal,
  setShowMassEditModal,
  massData,
  children,
}) => {
  const showPromiseConfirm = async () => {
    const response = new Promise((resolve) => {
      confirm({
        title: "Update Confirmation?",
        icon: <ExclamationCircleOutlined />,
        content:
          "Details entered here will be replacing the existing details entered on each card. Do you want to proceed?",

        onOk: () => {
          return resolve(true);
        },
        onCancel() {},
        okText: "Yes",
        cancelText: "No",
        okButtonProps: {
          type: "primary",
          shape: "round",
          style: {
            background: primaryDark,
            borderColor: primaryDark,
            width: "8rem",
            margin: 0,
          },
          size: "large",
        },
        cancelButtonProps: {
          type: "primary",
          shape: "round",
          style: {
            background: primaryDark,
            borderColor: primaryDark,
            width: "8rem",
            float: "right",
            marginLeft: ".5rem",
          },
          size: "large",
        },
      });
    });

    return response;
  };
  const [form] = Form.useForm();
  const { role } = useSelector((state) => state.auth.user);
  const handleOnChange = (changedValues, values) => {
    if (changedValues && changedValues.socialLinks) {
      const socialLinks = changedValues.socialLinks;

      socialLinks.forEach((item, index) => {
        if (item.linkValue) {
          const link =
            (values.socialLinks[index]?.prefix ?? "") + item.linkValue;
          form.setFieldValue(["socialLinks", index, "link"], link);
        }
      });
    }
  };

  const update = async (value) => {
    return Promise.all(
      massData.map(async (qrData) => {
        const reqQr = request.get(API + "/qr/profile/" + qrData?.link, {
          headers: { ...authHeader() },
        });

        const { data } = (await reqQr).data;
        const removeInvalidValue = (value) => {
          return (
            value === "" ||
            (_.isArray(value) && _.isEmpty(value)) ||
            _.isNil(value)
          );
        };

        const updateValue = _.omitBy(
          _.omit(value, ["socialLinks", "qrSetting"]),
          removeInvalidValue
        );

        const { socialLinks, qrSetting } = _.pick(value, [
          "socialLinks",
          "qrSetting",
        ]);

        const socialLinksMerged = socialLinks.map((social) => {
          if (social.active) {
            return social;
          }

          const origin = data.socialLinks.find((d) => d.name === social.name);
          social.link = origin?.link;

          return social;
        });

        const patchData = {
          ...updateValue,
          socialLinks: [...socialLinksMerged],
          qrSetting: {
            ...data.qrSetting,
            ..._.omitBy(qrSetting, removeInvalidValue),
          },
        };

        const _request = (qrData, d) =>
          request.put(API + "/qr/" + qrData?._id, d, {
            headers: { ...authHeader() },
          });

        return _request(qrData, patchData);
      })
    );
  };
  return (
    <Modal
      visible={showMassEditModal}
      destroyOnClose={true}
      maskClosable={false}
      title="Mass Edit WaveCards"
      footer={null}
      onCancel={() => {
        setShowMassEditModal(false);
        form.resetFields();
      }}
      width={650}
    >
      {children}
      <Form
        name="massEditForm"
        form={form}
        onValuesChange={handleOnChange}
        preserve={false}
        initialValues={INITIAL_DATA}
        onFinish={async (e) => {
          try {
            await update(e);

            form.resetFields();
            success({
              title: "Update Success",
              content: "",

              onOk: () => {
                setShowMassEditModal(false);
              },
              onCancel() {},
              okButtonProps: {
                type: "primary",
                shape: "round",
                style: {
                  background: primaryDark,
                  borderColor: primaryDark,
                  width: "8rem",
                  margin: 0,
                },
                size: "large",
              },
            });
          } catch (err) {
            console.error("error occurred", err);
            error({
              title: "Update Failed",
              content:
                "An error occurred while updating. Check the input then try again",

              onOk: () => {},
              onCancel() {},
              okButtonProps: {
                type: "primary",
                shape: "round",
                style: {
                  background: primaryDark,
                  borderColor: primaryDark,
                  width: "8rem",
                  margin: 0,
                },
                size: "large",
              },
            });
          }
        }}
      >
        <Collapse
          defaultActiveKey={[1, 2, 3, 4, 5, 6]}
          bordered={false}
          className="site-collapse-custom-panel"
          expandIcon={({ isActive }) => (
            <DoubleRightOutlined rotate={isActive ? 90 : 0} />
          )}
        >
          <Collapse.Panel header="Design and Customize" key={1}>
            <Theme form={form}></Theme>
          </Collapse.Panel>
          <Collapse.Panel header="Basic Information" key={2}>
            <Name></Name>
            <MobileNumber></MobileNumber>
            <PhoneNumber></PhoneNumber>
            <Email></Email>
            <JobTitle></JobTitle>
            <Company></Company>
            <Address form={form}></Address>
            <PostalAddress></PostalAddress>
            <Summary></Summary>
          </Collapse.Panel>
          <Collapse.Panel header="Images Upload" key={3}>
            <Gallery form={form}></Gallery>
          </Collapse.Panel>
          <Collapse.Panel header="Web Links" key={4}>
            <Websites form={form}></Websites>
          </Collapse.Panel>
          <Collapse.Panel header="Social Links" key={5}>
            <SocialLinks form={form}></SocialLinks>
          </Collapse.Panel>
          <Collapse.Panel
            header={
              role === WHITE_LABEL_ADMIN ? "Code Styles" : "Wave Code Styles"
            }
            key={6}
          >
            <WaveCodeStyle form={form}></WaveCodeStyle>
          </Collapse.Panel>
        </Collapse>
      </Form>
      <Col>
        <Row
          style={{
            marginTop: "2rem",
            justifyContent: "space-evenly",
          }}
        >
          <Button
            type="primary"
            shape="round"
            style={{
              display: "flex",
              background: primaryDark,
              borderColor: primaryDark,
              width: "8rem",
              justifyContent: "center",
            }}
            size="large"
            onClick={async (e) => {
              try {
                await showPromiseConfirm();
                await form.validateFields();
                form.submit();
              } catch (error) {
                console.error(error);
              }
            }}
          >
            Update
          </Button>
          <Button
            type="primary"
            shape="round"
            style={{
              display: "flex",
              background: primaryDark,
              borderColor: primaryDark,
              justifyContent: "center",

              width: "8rem",
            }}
            size="large"
            onClick={() => {
              form.resetFields();
              setShowMassEditModal(false);
            }}
          >
            Discard
          </Button>
        </Row>
      </Col>
    </Modal>
  );
};

export default MassEditModal;
